import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceApi, employeesApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  Clock, 
  Calendar as CalendarIcon, 
  Download,
  Play,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

export default function AttendancePage() {
  const { user, canAccessHR, isEmployee } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [daySummary, setDaySummary] = useState({});
  const [employees, setEmployees] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedEmployee, selectedMonth, selectedYear, selectedDay]);

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        month: selectedMonth,
        year: selectedYear
      };
      
      if (selectedEmployee && selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      
      // Calculate date range for selected month/year
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      let endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      
      // If a specific day is selected, filter only that day
      if (selectedDay) {
        params.start_date = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        params.end_date = params.start_date;
      } else {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      // Fetch attendance and today's status
      const [attendanceRes, todayRes] = await Promise.all([
        attendanceApi.getAll(params),
        attendanceApi.getToday()
      ]);

      setAttendance(attendanceRes.data || []);
      setTodayAttendance(todayRes.data);

      // Only fetch day summary and employees for HR
      if (canAccessHR) {
        try {
          const [daySummaryRes, empRes] = await Promise.all([
            attendanceApi.getDaySummary(selectedMonth, selectedYear),
            employeesApi.getAll()
          ]);
          setDaySummary(daySummaryRes.data || {});
          setEmployees(empRes.data || []);
        } catch (e) {
          console.log('Could not fetch HR-specific data');
        }
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    try {
      setClockLoading(true);
      const response = await attendanceApi.clockIn();
      toast.success(`Clocked in at ${response.data.time}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClockLoading(true);
      const response = await attendanceApi.clockOut();
      toast.success(`Clocked out at ${response.data.time}. Worked ${response.data.work_hours.toFixed(1)} hours`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Employee', 'Status', 'Clock In', 'Clock Out', 'Work Hours'];
    const rows = attendance.map(a => [
      a.date,
      a.employee_name,
      a.status,
      a.clock_in || '-',
      a.clock_out || '-',
      a.work_hours || 0
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${MONTHS.find(m => m.value === selectedMonth)?.label}_${selectedYear}.csv`;
    a.click();
    toast.success('Attendance exported successfully');
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: { class: 'bg-green-100 text-green-700 border-green-200', label: 'Present' },
      absent: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Absent' },
      on_leave: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Leave' },
      half_day: { class: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Half Day' },
      week_off: { class: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Week Off' },
      holiday: { class: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Holiday' }
    };
    const variant = variants[status] || variants.present;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  // Calculate overall stats from day summary
  const overallStats = Object.values(daySummary).reduce((acc, day) => {
    acc.present += day.present || 0;
    acc.absent += day.absent || 0;
    acc.onLeave += day.on_leave || 0;
    acc.halfDay += day.half_day || 0;
    acc.weekOff += day.week_off || 0;
    acc.holiday += day.holiday || 0;
    acc.totalHours += day.total_hours || 0;
    return acc;
  }, { present: 0, absent: 0, onLeave: 0, halfDay: 0, weekOff: 0, holiday: 0, totalHours: 0 });

  // Generate calendar days
  const getDaysInMonth = () => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
    return { firstDay, totalDays };
  };

  const { firstDay, totalDays } = getDaysInMonth();

  const handleDayClick = (day) => {
    if (selectedDay === day) {
      setSelectedDay(null); // Deselect
    } else {
      setSelectedDay(day);
    }
  };

  // Build day-wise trend data for the selected month from the attendance records
  const trendData = (() => {
    const dayMap = {};
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      dayMap[dateStr] = { date: dateStr, day: d, present: 0, absent: 0, on_leave: 0, half_day: 0 };
    }
    for (const a of attendance) {
      if (!a?.date) continue;
      const key = a.date.slice(0, 10);
      if (!dayMap[key]) continue;
      if (a.status === 'present') dayMap[key].present += 1;
      else if (a.status === 'absent') dayMap[key].absent += 1;
      else if (a.status === 'on_leave') dayMap[key].on_leave += 1;
      else if (a.status === 'half_day') dayMap[key].half_day += 1;
    }
    return Object.values(dayMap);
  })();

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Attendance</h1>
          <p className="page-description">Track daily attendance and work hours</p>
        </div>
        {canAccessHR && (
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Clock In/Out Card */}
      <Card className="border-2 border-[#1a365d]/20">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-[#1a365d]/10 flex items-center justify-center">
                <Clock className="h-8 w-8 text-[#1a365d]" />
              </div>
              <div>
                <p className="text-3xl font-bold">{currentTime.toLocaleTimeString('en-IN')}</p>
                <p className="text-muted-foreground">{currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {todayAttendance && (
                <div className="text-sm text-muted-foreground">
                  {todayAttendance.clocked_in && <span>In: {todayAttendance.clock_in_time}</span>}
                  {todayAttendance.clocked_out && <span className="ml-3">Out: {todayAttendance.clock_out_time}</span>}
                </div>
              )}
              {!todayAttendance?.clocked_in ? (
                <Button onClick={handleClockIn} disabled={clockLoading} className="bg-[#1a365d] hover:bg-[#0f2744]" data-testid="clock-in-btn">
                  <Play className="h-4 w-4 mr-2" />
                  Clock In
                </Button>
              ) : !todayAttendance?.clocked_out ? (
                <Button onClick={handleClockOut} disabled={clockLoading} variant="destructive" data-testid="clock-out-btn">
                  <XCircle className="h-4 w-4 mr-2" />
                  Clock Out
                </Button>
              ) : (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedMonth.toString()} onValueChange={(v) => { setSelectedMonth(parseInt(v)); setSelectedDay(null); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="month-filter">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(month => (
                  <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedYear.toString()} onValueChange={(v) => { setSelectedYear(parseInt(v)); setSelectedDay(null); }}>
              <SelectTrigger className="w-full sm:w-32" data-testid="year-filter">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canAccessHR && (
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-full sm:w-64" data-testid="employee-filter">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {selectedDay && (
              <Button variant="ghost" onClick={() => setSelectedDay(null)} className="text-muted-foreground">
                Clear Day Filter (Day {selectedDay})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Day-wise Calendar with Summary */}
      {canAccessHR && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear} - Daily Attendance
                </CardTitle>
                <CardDescription>Click on a day to filter the table below</CardDescription>
              </div>
              <div className="flex gap-3 text-xs flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Present</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Absent</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>Leave</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                  <span>Half Day</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                  <span>Week Off</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Overall Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1a365d]">{employees.length}</p>
                <p className="text-xs text-muted-foreground">Active Employees</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{overallStats.present}</p>
                <p className="text-xs text-muted-foreground">Present Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{overallStats.absent}</p>
                <p className="text-xs text-muted-foreground">Absent Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{overallStats.onLeave}</p>
                <p className="text-xs text-muted-foreground">Leave Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-500">{overallStats.halfDay}</p>
                <p className="text-xs text-muted-foreground">Half Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-500">{overallStats.weekOff}</p>
                <p className="text-xs text-muted-foreground">Week Offs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{overallStats.totalHours.toFixed(0)}h</p>
                <p className="text-xs text-muted-foreground">Total Hours</p>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Empty cells */}
              {[...Array(firstDay)].map((_, i) => (
                <div key={`empty-${i}`} className="h-20"></div>
              ))}
              
              {/* Day cells */}
              {[...Array(totalDays)].map((_, i) => {
                const day = i + 1;
                const dayData = daySummary[day] || {};
                const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() === 0 || 
                                  new Date(selectedYear, selectedMonth - 1, day).getDay() === 6;
                const isSelected = selectedDay === day;
                const hasData = Object.values(dayData).some(v => v > 0);
                
                return (
                  <div 
                    key={day}
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      "rounded-lg p-2 border transition-all cursor-pointer hover:shadow-md",
                      isWeekend ? "bg-slate-50 dark:bg-slate-800/30" : "bg-white dark:bg-slate-800",
                      isSelected && "ring-2 ring-[#1a365d] border-[#1a365d]",
                      !hasData && "opacity-50"
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={cn("text-sm font-semibold", isWeekend && "text-slate-500")}>
                        {day}
                      </span>
                    </div>
                    
                    {hasData ? (
                      <div className="space-y-0.5 text-[10px]">
                        {dayData.present > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-green-700 dark:text-green-400">{dayData.present}</span>
                          </div>
                        )}
                        {dayData.absent > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-red-700 dark:text-red-400">{dayData.absent}</span>
                          </div>
                        )}
                        {dayData.on_leave > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span className="text-amber-700 dark:text-amber-400">{dayData.on_leave}</span>
                          </div>
                        )}
                        {dayData.half_day > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                            <span className="text-orange-600 dark:text-orange-400">{dayData.half_day}</span>
                          </div>
                        )}
                        {(dayData.week_off > 0 || dayData.holiday > 0) && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                            <span className="text-slate-600 dark:text-slate-400">{(dayData.week_off || 0) + (dayData.holiday || 0)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-muted-foreground">-</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Trend chart */}
      <Card data-testid="attendance-trend-card">
        <CardHeader>
          <CardTitle className="text-lg">Attendance Trend</CardTitle>
          <CardDescription>
            Daily breakdown for {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-72 bg-muted/40 rounded-lg animate-pulse" />
          ) : trendData.every(d => d.present === 0 && d.absent === 0 && d.on_leave === 0 && d.half_day === 0) ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
              No attendance data to chart for this period.
            </div>
          ) : (
            <div style={{ width: '100%', height: 288 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="presentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="absentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="leaveGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                    labelFormatter={(d) => `Day ${d}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="present" name="Present" stroke="#14b8a6" strokeWidth={2} fill="url(#presentGrad)" />
                  <Area type="monotone" dataKey="absent" name="Absent" stroke="#ef4444" strokeWidth={2} fill="url(#absentGrad)" />
                  <Area type="monotone" dataKey="on_leave" name="On Leave" stroke="#f59e0b" strokeWidth={2} fill="url(#leaveGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Attendance Records {selectedDay ? `- ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedDay}, ${selectedYear}` : `- ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
          </CardTitle>
          <CardDescription>
            {selectedDay ? `Showing records for ${selectedDay}/${selectedMonth}/${selectedYear}` : 'View attendance history and work hours'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="responsive-table-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Work Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(6)].map((_, j) => (
                        <TableCell key={j}>
                          <div className="h-4 bg-muted rounded animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : attendance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No attendance records found
                    </TableCell>
                  </TableRow>
                ) : (
                  attendance.slice(0, 100).map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{new Date(record.date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell className="font-medium">{record.employee_name}</TableCell>
                      <TableCell>{getStatusBadge(record.status)}</TableCell>
                      <TableCell>{record.clock_in || '-'}</TableCell>
                      <TableCell>{record.clock_out || '-'}</TableCell>
                      <TableCell>{record.work_hours ? `${record.work_hours}h` : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {attendance.length > 100 && (
            <div className="p-4 text-center text-sm text-muted-foreground border-t">
              Showing first 100 of {attendance.length} records
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
